/**
 * ChecklistTemplateEditorPage - Editor do Template de Checklist
 *
 * - Página principal: seções (opcionais) e itens do rascunho, com reordenação,
 *   inativação e exclusão (só para o que nunca foi publicado).
 * - Modal de configurações: dados gerais, prazo, revisão e geolocalização.
 * - Publicar nova versão (resumo) e histórico (versões + timeline).
 *
 * @version 1.6.0
 * RELEASE: 2026-09-28 v1.6.0 - Encaminha o template ao upload privado da foto modelo.
 * RELEASE: 2026-09-27 v1.5.0 - Seção "Aceite (assinatura na tela)" no modal de configurações (Core de Aceites).
 * RELEASE: 2026-09-26 v1.4.0 - Modal de configurações alinhado ao padrão premium das anotações do chat.
 * RELEASE: 2026-09-26 v1.3.0 - Remove abas, move configurações para modal e compacta ações/status do cabeçalho.
 * RELEASE: 2026-09-26 v1.2.0 - Editor em largura integral, abas no padrão Configurações e botões compactos.
 * RELEASE: 2026-09-26 v1.1.0 - Campo obrigatório Tipo de Checklist em Dados gerais.
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial (Fase 1b)
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDown, ArrowLeft, ArrowUp, ClipboardList, Clock3, EyeOff, History, Eye, MapPin,
  Pencil, Plus, Rocket, Save, ShieldCheck, Trash2, Lock, Settings, X,
} from 'lucide-react';
import { ListPageLayout, ListPageHeader, ListPageContent } from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { InlineNotice } from '@/components/ui/inline-notice';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { usePermissions } from '@/hooks/usePermissions';
import {
  fieldTypeLabel, useChecklistTemplate, type ChecklistItem, type ChecklistSection,
} from '@/hooks/useChecklistTemplates';
import { ChecklistItemDialog } from '@/components/checklist/ChecklistItemDialog';
import { ChecklistPublishDialog } from '@/components/checklist/ChecklistPublishDialog';
import { ChecklistHistorySheet } from '@/components/checklist/ChecklistHistorySheet';
import { ChecklistTypeSelect } from '@/components/checklist/ChecklistTypesDialog';
import { FormSectionCard } from '@/components/crm/deals/components/FormSectionCard';
import { ChecklistAcceptanceConfigCard } from '@/components/checklist/ChecklistAcceptanceConfigCard';

export default function ChecklistTemplateEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermissions();
  const tpl = useChecklistTemplate(id);
  const allowEdit = canEdit('checklist_templates');
  const allowDelete = canDelete('checklist_templates');
  const allowPublish = canEdit('checklist_publish');

  const [itemDialog, setItemDialog] = useState<{ open: boolean; item: ChecklistItem | null; sectionId: string | null }>({ open: false, item: null, sectionId: null });
  const [publishOpen, setPublishOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newSection, setNewSection] = useState('');
  const [editingSection, setEditingSection] = useState<{ id: string; name: string } | null>(null);

  const template = tpl.data?.template;
  const sections = useMemo(() => (tpl.data?.sections ?? []).slice().sort((a, b) => a.sort_order - b.sort_order), [tpl.data]);
  const items = useMemo(() => (tpl.data?.items ?? []).slice().sort((a, b) => a.sort_order - b.sort_order), [tpl.data]);

  // Grupos: itens sem seção primeiro, depois cada seção
  const groups: { section: ChecklistSection | null; items: ChecklistItem[] }[] = useMemo(() => {
    const loose = items.filter((i) => !i.section_id);
    return [
      ...(loose.length ? [{ section: null, items: loose }] : []),
      ...sections.map((s) => ({ section: s, items: items.filter((i) => i.section_id === s.id) })),
    ];
  }, [items, sections]);

  /** Move uma seção para cima/baixo */
  const moveSection = (idx: number, dir: -1 | 1) => {
    const arr = sections.map((s) => s.id);
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    tpl.reorderSections.mutate(arr);
  };

  /** Move um item dentro do seu grupo */
  const moveItem = (group: ChecklistItem[], idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= group.length) return;
    const arr = group.slice();
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    // Mantém a posição relativa global usando os sort_order já existentes do grupo
    const orders = group.map((g) => g.sort_order).sort((a, b) => a - b);
    tpl.reorderItems.mutate(arr.map((it, k) => ({ id: it.id, section_id: it.section_id, sort_order: orders[k] })));
  };

  if (tpl.isLoading) {
    return <ListPageLayout><ListPageContent><Skeleton className="h-64 w-full" /></ListPageContent></ListPageLayout>;
  }
  if (!template) {
    return (
      <ListPageLayout>
        <ListPageContent><InlineNotice variant="error">Modelo não encontrado ou sem acesso.</InlineNotice></ListPageContent>
      </ListPageLayout>
    );
  }

  return (
    <ListPageLayout>
      <ListPageHeader
        title={template.name}
        description={template.current_version_number ? `Versão vigente: v${template.current_version_number}` : 'Ainda não publicado'}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <HeaderIconButton label="Voltar" onClick={() => navigate('/tenant/settings/checklist-templates')}>
              <ArrowLeft />
            </HeaderIconButton>
            {template.has_unpublished_changes && <Badge variant="outline">Alterações não publicadas</Badge>}
            <Badge variant="outline" className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider">
              <StatusDot tone={template.status === 'active' ? 'emerald' : 'muted'} />
              {template.status === 'active' ? 'Ativo' : 'Inativo'}
            </Badge>
            <HeaderIconButton label="Histórico" onClick={() => setHistoryOpen(true)}>
              <History />
            </HeaderIconButton>
            <HeaderIconButton label="Configurações" onClick={() => setSettingsOpen(true)}>
              <Settings />
            </HeaderIconButton>
            {allowEdit && (
              <Button variant="outline" size="xs"
                onClick={() => tpl.setStatus.mutate(template.status === 'active' ? 'inactive' : 'active')}>
                {template.status === 'active' ? 'Inativar' : 'Ativar'}
              </Button>
            )}
            {allowPublish && (
              <Button
                size="xs"
                className="bg-success text-success-foreground hover:bg-success/90"
                onClick={() => setPublishOpen(true)}
                disabled={!template.has_unpublished_changes && !!template.current_version_number}
              >
                <Rocket className="mr-1.5 h-4 w-4" /> Publicar
              </Button>
            )}
          </div>
        }
      />

      <ListPageContent>
        {/* A página permanece dedicada aos itens; configurações são abertas pelo cabeçalho. */}
        <div className="space-y-4">
              <InlineNotice variant="info">
                As alterações ficam em rascunho até a publicação. Itens e seções já publicados não podem ser excluídos, apenas inativados.
              </InlineNotice>

              {groups.map((g, gi) => {
                const s = g.section;
                const sIdx = s ? sections.findIndex((x) => x.id === s.id) : -1;
                return (
                  <section key={s?.id ?? 'loose'} className={`rounded-lg border ${s && !s.is_active ? 'opacity-60' : ''}`}>
                    <header className="flex items-center gap-2 border-b px-4 py-2">
                      {s && editingSection?.id === s.id ? (
                        <Input autoFocus value={editingSection.name} maxLength={150} className="h-8"
                          onChange={(e) => setEditingSection({ id: s.id, name: e.target.value })}
                          onBlur={() => { if (editingSection.name.trim()) tpl.saveSection.mutate({ id: s.id, name: editingSection.name.trim(), description: s.description }); setEditingSection(null); }}
                          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
                      ) : (
                        <h3 className="flex-1 text-sm font-semibold">
                          {s ? s.name : 'Sem seção'}
                          {s && !s.is_active && <Badge variant="secondary" className="ml-2">Inativa</Badge>}
                          {s?.ever_published && <Lock className="ml-1 inline h-3 w-3 text-muted-foreground" />}
                        </h3>
                      )}
                      {s && allowEdit && (
                        <div className="flex items-center gap-0.5">
                          <IconBtn label="Subir seção" onClick={() => moveSection(sIdx, -1)} disabled={sIdx === 0}><ArrowUp /></IconBtn>
                          <IconBtn label="Descer seção" onClick={() => moveSection(sIdx, 1)} disabled={sIdx === sections.length - 1}><ArrowDown /></IconBtn>
                          <IconBtn label="Renomear seção" onClick={() => setEditingSection({ id: s.id, name: s.name })}><Pencil /></IconBtn>
                          <IconBtn label={s.is_active ? 'Inativar seção' : 'Reativar seção'} onClick={() => tpl.setSectionActive.mutate({ id: s.id, active: !s.is_active })}>
                            {s.is_active ? <EyeOff /> : <Eye />}
                          </IconBtn>
                          {allowDelete && !s.ever_published && !g.items.some((i) => i.ever_published) && (
                            <IconBtn label="Excluir seção" onClick={() => tpl.deleteSection.mutate(s.id)}><Trash2 /></IconBtn>
                          )}
                        </div>
                      )}
                    </header>

                    <ul className="divide-y">
                      {g.items.length === 0 && <li className="px-4 py-3 text-xs text-muted-foreground">Nenhum item.</li>}
                      {g.items.map((it, idx) => (
                        <li key={it.id} className={`flex items-center gap-3 px-4 py-2 ${!it.is_active ? 'opacity-60' : ''}`}>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">
                              {it.name}{it.is_required && <span className="text-destructive"> *</span>}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {fieldTypeLabel(it.field_type)}
                              {it.requires_approval ? ' · exige aprovação' : ''}
                              {!it.is_active ? ' · inativo' : ''}
                              {it.ever_published ? ' · publicado' : ' · novo'}
                            </p>
                          </div>
                          {allowEdit && (
                            <div className="flex items-center gap-0.5">
                              <IconBtn label="Subir item" onClick={() => moveItem(g.items, idx, -1)} disabled={idx === 0}><ArrowUp /></IconBtn>
                              <IconBtn label="Descer item" onClick={() => moveItem(g.items, idx, 1)} disabled={idx === g.items.length - 1}><ArrowDown /></IconBtn>
                              <IconBtn label="Editar item" onClick={() => setItemDialog({ open: true, item: it, sectionId: it.section_id })}><Pencil /></IconBtn>
                              <IconBtn label={it.is_active ? 'Inativar item' : 'Reativar item'} onClick={() => tpl.setItemActive.mutate({ id: it.id, active: !it.is_active })}>
                                {it.is_active ? <EyeOff /> : <Eye />}
                              </IconBtn>
                              {allowDelete && !it.ever_published && (
                                <IconBtn label="Excluir item" onClick={() => tpl.deleteItem.mutate(it.id)}><Trash2 /></IconBtn>
                              )}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                    {allowEdit && (
                      <div className="px-4 py-2">
                        <Button variant="ghost" size="xs" onClick={() => setItemDialog({ open: true, item: null, sectionId: s?.id ?? null })}>
                          <Plus className="mr-1.5 h-4 w-4" /> Adicionar item
                        </Button>
                      </div>
                    )}
                  </section>
                );
              })}

              {allowEdit && (
                <div className="flex flex-wrap items-center gap-2">
                  <Input className="max-w-xs" placeholder="Nome da nova seção" value={newSection} maxLength={150}
                    onChange={(e) => setNewSection(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && newSection.trim()) { tpl.saveSection.mutate({ name: newSection.trim() }); setNewSection(''); } }} />
                  <Button size="xs" variant="outline" disabled={!newSection.trim()}
                    onClick={() => { tpl.saveSection.mutate({ name: newSection.trim() }); setNewSection(''); }}>
                    <Plus className="mr-1.5 h-4 w-4" /> Seção
                  </Button>
                  {groups.length === 0 && (
                    <Button size="xs" variant="outline" onClick={() => setItemDialog({ open: true, item: null, sectionId: null })}>
                      <Plus className="mr-1.5 h-4 w-4" /> Item sem seção
                    </Button>
                  )}
                </div>
              )}
        </div>
      </ListPageContent>

      {/* Configurações em modal para manter a página principal focada nos itens. */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="flex h-[90dvh] max-h-[90dvh] w-[calc(100vw-2rem)] max-w-3xl flex-col gap-0 overflow-hidden p-0">
          {/* Cabeçalho premium compartilhado com os modais de anotações do chat. */}
          <div className="shrink-0 border-b bg-gradient-to-r from-primary/5 via-primary/3 to-transparent px-6 py-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                  <Settings className="h-3.5 w-3.5 text-primary" />
                </span>
                Configurações do template
              </DialogTitle>
              <DialogDescription className="text-xs">
                Defina os dados gerais, prazos, revisão e controles de geolocalização.
              </DialogDescription>
            </DialogHeader>
          </div>
          {settingsOpen && (
            <TemplateSettingsForm
              template={template}
              disabled={!allowEdit}
              saving={tpl.saveTemplate.isPending}
              onCancel={() => setSettingsOpen(false)}
              onSave={async (data) => {
                await tpl.saveTemplate.mutateAsync(data);
                setSettingsOpen(false);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <ChecklistItemDialog
        open={itemDialog.open}
        onOpenChange={(o) => setItemDialog((s) => ({ ...s, open: o }))}
        item={itemDialog.item}
        defaultSectionId={itemDialog.sectionId}
        sections={sections.filter((s) => s.is_active)}
        templateId={template.id}
        saving={tpl.saveItem.isPending}
        onSave={(data) => tpl.saveItem.mutateAsync({ id: itemDialog.item?.id, data })}
      />
      <ChecklistPublishDialog open={publishOpen} onOpenChange={setPublishOpen} templateId={template.id}
        publishing={tpl.publish.isPending} onConfirm={() => tpl.publish.mutateAsync(undefined)} />
      <ChecklistHistorySheet open={historyOpen} onOpenChange={setHistoryOpen} templateId={template.id} />
    </ListPageLayout>
  );
}

/** Botão de ícone compacto com rótulo acessível */
function IconBtn({ label, onClick, disabled, children }: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 [&_svg]:h-3.5 [&_svg]:w-3.5"
      aria-label={label} title={label} onClick={onClick} disabled={disabled}>
      {children}
    </Button>
  );
}

/** Ação iconográfica do cabeçalho com tooltip e área de clique estável. */
function HeaderIconButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 [&_svg]:h-4 [&_svg]:w-4" aria-label={label} onClick={onClick}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

// -----------------------------------------------------------------------------
// Formulário de configurações do modelo
// -----------------------------------------------------------------------------
function TemplateSettingsForm({ template, disabled, saving, onSave, onCancel }: {
  template: NonNullable<ReturnType<typeof useChecklistTemplate>['data']>['template'];
  disabled: boolean; saving: boolean; onSave: (d: Record<string, unknown>) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [f, setF] = useState(template);
  useEffect(() => setF(template), [template]);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((p) => ({ ...p, [k]: v }));

  // Conversão horas <-> minutos para os prazos relativos
  const h = (m: number | null) => (m == null ? '' : String(m / 60));
  const toMin = (v: string) => (v === '' ? null : Math.round(Number(v) * 60));

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-6 py-5">
       <FormSectionCard title="Dados gerais" icon={ClipboardList}>
        <div className="space-y-1.5"><Label>Nome</Label>
          <Input value={f.name} maxLength={150} disabled={disabled} onChange={(e) => set('name', e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Descrição</Label>
          <Textarea value={f.description ?? ''} maxLength={2000} rows={3} disabled={disabled} onChange={(e) => set('description', e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Tipo de Checklist *</Label>
          <ChecklistTypeSelect value={f.type_id ?? ''} keepId={template.type_id} disabled={disabled} onChange={(v) => set('type_id', v)} /></div>
        <div className="space-y-1.5"><Label>Uso</Label>
          <Input value="Vistoria de veículo" disabled />
          <p className="text-xs text-muted-foreground">Outros usos (ex.: Ordem de Serviço) serão liberados com os próximos módulos.</p></div>
       </FormSectionCard>

       <FormSectionCard title="Prazo" icon={Clock3}>
        <Select value={f.deadline_mode} disabled={disabled} onValueChange={(v) => set('deadline_mode', v as typeof f.deadline_mode)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="free">Livre (sem prazo)</SelectItem>
            <SelectItem value="relative">Tempo para iniciar e concluir</SelectItem>
            <SelectItem value="fixed">Data fixa definida ao criar a vistoria</SelectItem>
          </SelectContent>
        </Select>
        {f.deadline_mode === 'relative' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Iniciar em até (horas, após criação)</Label>
              <Input type="number" min={0} step="0.5" disabled={disabled} value={h(f.start_within_min)} onChange={(e) => set('start_within_min', toMin(e.target.value))} /></div>
            <div className="space-y-1.5"><Label>Concluir em até (horas, após início)</Label>
              <Input type="number" min={0} step="0.5" disabled={disabled} value={h(f.finish_within_min)} onChange={(e) => set('finish_within_min', toMin(e.target.value))} /></div>
          </div>
        )}
        {f.deadline_mode !== 'free' && (
          <Toggle label="Permitir continuar a vistoria após o prazo" checked={f.allow_overdue} disabled={disabled} onChange={(v) => set('allow_overdue', v)} />
        )}
       </FormSectionCard>

       {/* Link externo: prazo próprio, separado do prazo da vistoria */}
       <FormSectionCard title="Link externo" icon={Clock3}>
        <div className="space-y-1.5"><Label>Validade do link externo (dias)</Label>
          <Input type="number" min={1} max={90} disabled={disabled} value={f.external_link_validity_days ?? 7}
            onChange={(e) => set('external_link_validity_days', Math.min(90, Math.max(1, Number(e.target.value) || 1)))} />
          <p className="text-xs text-muted-foreground">Por quanto tempo o link enviado ao cliente fica ativo (1 a 90 dias). O link também encerra quando a vistoria é concluída, cancelada ou atualizada de versão.</p></div>
       </FormSectionCard>

       <FormSectionCard title="Revisão e assinatura" icon={ShieldCheck}>
        <Toggle label="Exige revisão antes de aprovar" checked={f.requires_review} disabled={disabled} onChange={(v) => set('requires_review', v)} />
        <Toggle label="Quem executou pode aprovar a própria vistoria" checked={f.allow_self_review} disabled={disabled} onChange={(v) => set('allow_self_review', v)} />
        <Toggle label="Exigir foto em item reprovado" checked={f.require_photo_on_reject} disabled={disabled} onChange={(v) => set('require_photo_on_reject', v)} />
       </FormSectionCard>

       <FormSectionCard title="Geolocalização (prevenção de fraude)" icon={MapPin}>
        <Select value={f.geo_mode} disabled={disabled} onValueChange={(v) => set('geo_mode', v as typeof f.geo_mode)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="off">Desligada</SelectItem>
            <SelectItem value="submit">Somente no envio</SelectItem>
            <SelectItem value="item">Em cada item</SelectItem>
            <SelectItem value="item_and_submit">Em cada item e no envio (recomendado)</SelectItem>
          </SelectContent>
        </Select>
        {f.geo_mode !== 'off' && (
          <>
            <Toggle label="Localização obrigatória (sem ela o item não é salvo)" checked={f.geo_required} disabled={disabled} onChange={(v) => set('geo_required', v)} />
            <div className="space-y-1.5"><Label>Distância máxima entre os pontos da mesma vistoria (metros)</Label>
              <Input type="number" min={0} disabled={disabled} value={f.geo_max_radius_m ?? ''} placeholder="Ex.: 200"
                onChange={(e) => set('geo_max_radius_m', e.target.value === '' ? null : Number(e.target.value))} />
              <p className="text-xs text-muted-foreground">Acima disso a vistoria recebe alerta de possível fraude.</p></div>
          </>
        )}
       </FormSectionCard>

       <ChecklistAcceptanceConfigCard templateId={template.id} requiresReview={f.requires_review} disabled={disabled} />

      </div>

      <div className="flex shrink-0 justify-end gap-2 border-t bg-muted/10 px-6 py-3">
        <Button type="button" size="sm" variant="outline" className="h-7 px-2 sm:px-3" onClick={onCancel}>
          <X className="mr-1 h-4 w-4" /> Cancelar
        </Button>
        {!disabled && (
          <Button size="sm" className="h-7 px-2 sm:px-3" disabled={saving || !f.name.trim() || !f.type_id} onClick={() => onSave({
            name: f.name.trim(), type_id: f.type_id, description: f.description?.trim() || null,
            deadline_mode: f.deadline_mode, start_within_min: f.start_within_min, finish_within_min: f.finish_within_min,
            allow_overdue: f.allow_overdue, requires_review: f.requires_review, allow_self_review: f.allow_self_review,
            require_photo_on_reject: f.require_photo_on_reject,
            geo_mode: f.geo_mode, geo_required: f.geo_required, geo_max_radius_m: f.geo_max_radius_m,
            external_link_validity_days: f.external_link_validity_days ?? 7,
          })}><Save className="mr-1 h-4 w-4" />{saving ? 'Salvando...' : 'Salvar configurações'}</Button>
        )}
      </div>
    </div>
  );
}

function Toggle({ label, checked, disabled, onChange }: { label: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-4 text-sm">
      <span>{label}</span>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </label>
  );
}
